<template>
  <div
    :class="[$style.base, { [$style.expanded]: showGiftcardCodeField }]"
    v-test="'giftcard-form'"
  >
    <div
      v-if="
        giftcardCode &&
        pricing?.quote?.giftcard?.total_amount &&
        !giftcardCodeError
      "
      :class="$style.redeemGiftcard"
      v-test="'giftcardCodeSuccess'"
    >
      <div :class="$style.giftcardDetails">
        <div :class="$style.label">
          {{ $t('confirmation.giftcard_remaining') }}
        </div>
        <div>{{ currency(pricing.quote.giftcard.available_amount || 0) }}</div>
        <div v-if="formattedDate" :class="$style.label">
          {{ $t('confirmation.giftcard_expiration') }}
        </div>
        <div v-if="formattedDate">{{ formattedDate }}</div>
      </div>
      <div :class="$style.redeemForm">
        <div>
          {{ $t('confirmation.giftcard_form_disclaimer') }}
        </div>
        <div :class="$style.giftcardRedeemInputWrapper">
          <BaseCurrencyInput
            :class="$style.redeemInput"
            v-model="giftcardCodeRedeemInput"
            v-on:keydown.enter.prevent="redeemGiftcardCode"
            :disabled="isRedeemLoading"
            name="giftcardRedeemAmount"
            :label="$t('confirmation.giftcard')"
            v-test="'giftcardRedeemInput'"
          />
          <BaseButton
            :class="$style.applyGiftcardButton"
            @click="redeemGiftcardCode"
            v-test="'redeemCodeButton'"
            v-if="showGiftcardCodeField"
            :buttonColor="buttonColor"
            :isLoading="isLoading"
          >
            {{ $t('confirmation.redeem') }}
          </BaseButton>
          <IconClose
            v-if="showGiftcardCodeField"
            :class="$style.closeButton"
            @click="closeAndCleanInput"
            v-test="'giftcard-form-btn-close'"
          />
        </div>
        <div
          v-if="giftcardRedeemedAmount > 0"
          :class="$style.success"
          v-test="'redeemedAmountSuccess'"
        >
          {{
            $t('confirmation.redeemed_amount_applied', {
              amount: currency(giftcardRedeemedAmount)
            })
          }}
        </div>
      </div>
    </div>
    <div v-else>
      <div v-if="showGiftcardCodeField" :class="$style.giftcardFieldWrapper">
        <div :class="$style.giftcardInputWrapper">
          <BaseFormInput
            v-if="showGiftcardCodeField"
            v-model="giftcardCodeInput"
            v-on:keydown.enter.prevent="fetchGiftcardCode"
            :disabled="!isLoading"
            name="giftcardCode"
            :label="$t('confirmation.giftcard')"
            v-test="'giftcardCodeInput'"
          />
          <BaseButton
            :class="$style.applyGiftcardButton"
            @click="fetchGiftcardCode"
            v-test="'applyGiftcardCodeButton'"
            v-if="showGiftcardCodeField"
            :buttonColor="buttonColor"
            :isLoading="isLoading"
          >
            {{ $t(`confirmation.${applyCodeKey}`) }}
          </BaseButton>
          <IconClose
            :class="$style.closeButton"
            v-if="showGiftcardCodeField"
            @click="closeAndCleanInput"
            v-test="'giftcard-form-btn-close'"
          />
        </div>
      </div>
      <div
        v-test="'btn-add-giftcard-code'"
        v-else
        :class="$style.link"
        :style="{ color: widgetSettings.color }"
        @click="showGiftcardCodeField = true"
      >
        {{ $t('confirmation.add_giftcard_code') }}
      </div>
      <div
        v-if="giftcardCodeError"
        :class="$style.error"
        v-test="'giftcardCodeError'"
      >
        {{ $t('errors.giftcard_code_not_valid') }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { currency } from '@/helpers';
import { useBookingStore } from '@/stores/booking';
import type { BookingFormData } from '@/types';
import { storeToRefs } from 'pinia';
import { computed, inject, nextTick, ref, watch } from 'vue';

import widgetSettings from '@/widget-settings';
import { usePrincing } from './pricing';

import BaseButton from '@/components/BaseButton.vue';
import BaseCurrencyInput from '@/components/BaseCurrencyInput.vue';
import BaseFormInput from '@/components/base-form-input/index.vue';
import IconClose from '@/components/IconClose.vue';
import dayjs from 'dayjs';
import { useI18n } from 'vue-i18n';

const { giftcardCode, giftcardRedeemedAmount } = storeToRefs(useBookingStore());
const { pricing } = storeToRefs(useBookingStore());
const formData = inject<BookingFormData>('bookingFormData')!;

const isLoading = ref(false);
const isRedeemLoading = ref(false);
const showGiftcardCodeField = ref(false);

const { d } = useI18n();
const formattedDate = computed(() =>
  pricing.value?.quote?.giftcard?.expires_at
    ? d(dayjs(pricing.value.quote.giftcard.expires_at).toDate())
    : null
);

const alreadyHasGiftcardCode = computed(
  () =>
    giftcardCode.value &&
    pricing.value?.quote.giftcard &&
    pricing.value.quote.giftcard.total_amount
);

const giftcardCodeError = computed(() =>
  pricing.value?.errors.find((e) => e.type === 'giftcard_code_invalid')
);
const buttonColor = computed(() =>
  alreadyHasGiftcardCode.value && !giftcardCodeError.value
    ? '#2ac77c'
    : widgetSettings.color
);
const applyCodeKey = computed(() =>
  alreadyHasGiftcardCode.value && !giftcardCodeError.value
    ? 'code_applied'
    : 'apply_discount_code'
);
const giftcardCodeInput = ref('');
const maxRedeemableAmount = ref(0);

const fetchPrice = (setMax = false) => {
  const { getPrice } = usePrincing();
  giftcardCode.value = {
    code: giftcardCodeInput.value
  };
  getPrice({ email: formData.email })
    .then(() => {
      giftcardCode.value = {
        ...pricing.value?.quote?.giftcard,
        code: giftcardCodeInput.value
      };
    })
    .finally(() => {
      maxRedeemableAmount.value = calculateMaxRedeemableAmount();
      if (setMax) {
        giftcardRedeemedAmount.value = maxRedeemableAmount.value;
      }
      isLoading.value = false;
      isRedeemLoading.value = false;
    });
};

const fetchGiftcardCode = (e: any) => {
  e.preventDefault();
  isLoading.value = true;
  fetchPrice(true);
};

const giftcardCodeRedeemInput = ref(0);
const redeemGiftcardCode = (e: any) => {
  e.preventDefault();
  isRedeemLoading.value = true;
  maxRedeemableAmount.value = calculateMaxRedeemableAmount();
  giftcardCodeRedeemInput.value = Math.min(
    giftcardCodeRedeemInput.value,
    maxRedeemableAmount.value
  );
  giftcardRedeemedAmount.value = giftcardCodeRedeemInput.value;
  fetchPrice();
};

const calculateMaxRedeemableAmount = () => {
  if (
    pricing.value?.quote?.total &&
    pricing.value?.quote?.giftcard?.available_amount
  ) {
    return pricing.value?.quote?.giftcard?.available_amount >
      pricing.value.quote.total
      ? pricing.value?.quote.total
      : pricing.value?.quote?.giftcard?.available_amount;
  }
  return 0;
};

watch(pricing, () => {
  maxRedeemableAmount.value = calculateMaxRedeemableAmount();
});

watch(maxRedeemableAmount, (value) => {
  if (giftcardRedeemedAmount.value > value) {
    giftcardRedeemedAmount.value = value;
  }
  giftcardCodeRedeemInput.value = value;
});

const clearGiftcardCode = () => {
  giftcardCodeInput.value = '';
  giftcardCode.value = undefined;
  giftcardRedeemedAmount.value = 0;
};

const closeAndCleanInput = () => {
  showGiftcardCodeField.value = false;
  clearGiftcardCode();
  fetchPrice();
};
</script>

<style lang="scss" module>
.base {
  margin-top: $spacing;
  display: flex;
  flex-direction: column;

  &:not(.expanded) {
    display: inline-block;
  }
  &.expanded {
    width: 100%;
  }
}
.button {
  margin-top: $spacing * 2;
  width: 100%;
}

.link {
  @include link;
  background: transparent;
  margin-bottom: $spacing;
  text-decoration: none;
}

.giftcardFieldWrapper {
  display: flex;
  align-items: center;
}

.closeButton {
  cursor: pointer;
  margin-left: $spacing * 0.5;
  margin-bottom: $spacing;
}

.giftcardInputWrapper {
  flex-grow: 1;
  display: flex;
  position: relative;
  align-items: center;
}
.redeemInput {
  margin-bottom: $spacing;
}

.giftcardRedeemInputWrapper {
  flex-grow: 1;
  display: flex;
  position: relative;
  align-items: center;
}

.applyGiftcardButton {
  white-space: nowrap;
  margin: 0 $spacing * 0.5 $spacing;
}

.error {
  color: $color-error;
  font-size: 13px;
  margin-top: -$spacing * 0.5;
  margin-bottom: $spacing * 1.2;
}

.success {
  color: $color-success;
  font-size: 14px;
  margin-top: -$spacing * 0.5;
  margin-bottom: $spacing * 1.2;
}

.redeemGiftcard {
  flex-grow: 1;
  display: flex;
  align-items: stretch;
  gap: $spacing;
  flex-wrap: wrap;
}

.giftcardDetails {
  flex: 1;
  display: flex;
  width: 171px;
  padding: $spacing;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  border-radius: 8px;
  background: #fdedf9;
}

.label {
  white-space: nowrap;
  color: #9c9aa0;
  font-feature-settings:
    'liga' off,
    'clig' off;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}
.redeemForm {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: $spacing;
}
</style>
